import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  FilePlus2,
  PlayCircle,
  UserCheck,
  CheckCircle2,
  XCircle,
  CircleDot,
  MapPin,
  Clock3,
  User,
  Hash,
  Workflow,
  StickyNote,
  Gauge,
  Download,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import moment from "moment";
import AppContext from "../../context/AppContext";
import { fetchTaskRoadmap, generateTaskPdfLink } from "../../service/Task";
import { toast } from "../../utils/toast";
import translations from "../../components/common/Translations";
import StatusPill from "../../components/common/StatusPill";
import CommonTooltip from "../../components/common/CommonTooltip";
import { isRTL } from "../../utils/rtl";

const ANIMATION_MS = 300;

const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error ??
  error?.message;

// Per-event icon + accent colors for the timeline rail. Unknown//future event
// codes fall through to a neutral dot rather than breaking the rail.
const EVENT_STYLES = {
  CREATED: { Icon: FilePlus2, light: "bg-blue-50 text-blue-600 border-blue-200", dark: "bg-blue-500/15 text-blue-400 border-blue-500/30", rail: "bg-blue-400" },
  STARTED: { Icon: PlayCircle, light: "bg-amber-50 text-amber-600 border-amber-200", dark: "bg-amber-500/15 text-amber-400 border-amber-500/30", rail: "bg-amber-400" },
  ASSIGNED: { Icon: UserCheck, light: "bg-violet-50 text-violet-600 border-violet-200", dark: "bg-violet-500/15 text-violet-400 border-violet-500/30", rail: "bg-violet-400" },
  COMPLETED: { Icon: CheckCircle2, light: "bg-emerald-50 text-emerald-600 border-emerald-200", dark: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", rail: "bg-emerald-400" },
  FAILED: { Icon: XCircle, light: "bg-red-50 text-red-600 border-red-200", dark: "bg-red-500/15 text-red-400 border-red-500/30", rail: "bg-red-400" },
  RECALLED: { Icon: AlertCircle, light: "bg-orange-50 text-orange-600 border-orange-200", dark: "bg-orange-500/15 text-orange-400 border-orange-500/30", rail: "bg-orange-400" },
};

const DEFAULT_EVENT_STYLE = {
  Icon: CircleDot,
  light: "bg-slate-50 text-slate-500 border-slate-200",
  dark: "bg-gray-700/60 text-gray-300 border-gray-600",
  rail: "bg-slate-300",
};

// A COMPLETED event can still be an unsuccessful outcome ("Not completed
// successfully by ..." with meta.success === false) — render those in the
// failed style so a red-flagged task doesn't read as a green tick.
const getEventStyle = (item) => {
  const event = String(item?.event || "").toUpperCase();
  if (event === "COMPLETED" && item?.meta?.success === false) return EVENT_STYLES.FAILED;
  return EVENT_STYLES[event] || DEFAULT_EVENT_STYLE;
};

const DATE_TIME_FORMAT = "DD MMM YYYY, hh:mm A";

// parseZone (not plain moment()) is load-bearing: the backend sends timestamps
// with its own UTC offset (e.g. "2026-07-30T18:28:10+04:00"). Plain moment()
// would re-render that instant in the *viewer's* timezone, so a browser on IST
// (+05:30) shifts 18:28 to 19:58. parseZone keeps the offset the server sent,
// so the wall-clock time on screen matches the stored value. Strings with no
// offset are kept as-is too.
const formatDateTime = (value) => {
  if (!value) return "-";
  const m = moment.parseZone(value);
  return m.isValid() ? m.format(DATE_TIME_FORMAT) : String(value);
};

// Split date/time fields are already plain wall-clock values, so they're
// combined and formatted as-is with no timezone maths. `at` is the older
// single-timestamp shape, kept as a fallback.
const formatDateAndTime = (date, time, isoFallback) => {
  if (date && time) {
    const m = moment(`${date} ${time}`, "YYYY-MM-DD HH:mm:ss");
    if (m.isValid()) return m.format(DATE_TIME_FORMAT);
  }
  if (date) {
    const m = moment(date, "YYYY-MM-DD");
    if (m.isValid()) return m.format("DD MMM YYYY");
  }
  return isoFallback ? formatDateTime(isoFallback) : "";
};

const formatEventStamp = (item) => formatDateAndTime(item?.date, item?.time, item?.at);

const hasValue = (value) => value !== undefined && value !== null && value !== "";

// Actors/targets come back as { userId, name }; show both so two users with the
// same display name are still distinguishable. Falls back to whichever half the
// payload actually has.
const formatPerson = (person) => {
  if (!person) return "";
  const name = hasValue(person.name) ? String(person.name) : "";
  const id = hasValue(person.userId) ? `#${person.userId}` : "";
  if (name && id) return `${name} (${id})`;
  return name || id;
};

// Last path segment of the URL, query string stripped — used as the link text
// so the column shows something readable instead of a 200-char signed S3 URL.
const getFileNameFromUrl = (url) => {
  const withoutQuery = String(url).split(/[?#]/)[0];
  const segment = withoutQuery.split("/").filter(Boolean).pop() || String(url);
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

// photoLinks entries may be plain URL strings or objects carrying the workflow
// field they were captured for. Both are flattened to { label, url, fileName };
// string entries fall back to "Photos 1", "Photos 2", ... for the field column.
const normalizePhotoLinks = (rawPhotoLinks, fallbackLabel) =>
  (Array.isArray(rawPhotoLinks) ? rawPhotoLinks : [])
    .map((photo, index) => {
      const url =
        typeof photo === "string"
          ? photo
          : photo?.url ?? photo?.link ?? photo?.photoLink ?? photo?.imageUrl ?? "";
      if (!hasValue(url)) return null;
      const label =
        typeof photo === "string"
          ? `${fallbackLabel} ${index + 1}`
          : (photo?.fieldName ??
            photo?.field ??
            photo?.name ??
            photo?.label ??
            photo?.title ??
            photo?.key ??
            `${fallbackLabel} ${index + 1}`);
      return { label: String(label), url: String(url), fileName: getFileNameFromUrl(url) };
    })
    .filter(Boolean);

const isPlainObject = (value) =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// Scalar field values arrive as strings/numbers/booleans; non-photo multi-selects
// can still come back as arrays, so those are joined into one readable line.
const formatFieldValue = (value) => {
  if (Array.isArray(value)) return value.filter(hasValue).map(formatFieldValue).join(", ");
  if (isPlainObject(value)) return JSON.stringify(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

// "signalDbm" -> "Signal Dbm", "captured_at" -> "Captured At". Object-valued
// answers carry raw backend keys, so they're humanised before display.
const humanizeKey = (key) =>
  String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

// `taskCompletionDetails` is the completion-info payload: one entry per workflow
// question, in queNo order, each tagged with whether its value is a photo. Photo
// fields (file / multi_image) become one or more links; everything else renders
// as plain text. Fields with no captured value are dropped so the table only
// shows what the field user actually filled in.
const normalizeCompletionFields = (rawFields) =>
  (Array.isArray(rawFields) ? rawFields : [])
    .map((field, index) => {
      if (!field) return null;
      const label = hasValue(field.fieldName) ? String(field.fieldName) : `#${field.queNo ?? index + 1}`;
      const rawValue = field.value;
      const base = {
        key: `${field.queNo ?? index}-${label}`,
        queNo: hasValue(field.queNo) ? field.queNo : index + 1,
        label,
      };

      if (field.isPhoto) {
        const urls = (Array.isArray(rawValue) ? rawValue : [rawValue])
          .filter(hasValue)
          .map(String);
        if (urls.length === 0) return null;
        return {
          ...base,
          isPhoto: true,
          links: urls.map((url) => ({ url, fileName: getFileNameFromUrl(url) })),
        };
      }

      // Composite answers (e.g. signal_quality) come back as an object — those
      // render as one "Key: value" line per entry instead of a single string.
      if (isPlainObject(rawValue)) {
        const entries = Object.entries(rawValue)
          .filter(([, entryValue]) => hasValue(entryValue))
          .map(([entryKey, entryValue]) => ({
            key: entryKey,
            label: humanizeKey(entryKey),
            text: formatFieldValue(entryValue),
          }));
        if (entries.length === 0) return null;
        return { ...base, isPhoto: false, entries };
      }

      const text = hasValue(rawValue) ? formatFieldValue(rawValue) : "";
      if (!hasValue(text)) return null;
      // Email answers stay clickable (mailto:) instead of rendering as dead text.
      const isEmail = String(field.fieldType || "").toLowerCase() === "email" && !Array.isArray(rawValue);
      return { ...base, isPhoto: false, text, mailto: isEmail ? `mailto:${text}` : "" };
    })
    .filter(Boolean);

// Older roadmap payloads only carry `photoLinks`; map those into the same shape
// so the completion-info table still renders against a pre-`fields` backend.
const photoLinksAsCompletionFields = (photoLinks) =>
  photoLinks.map((photo, index) => ({
    key: `photo-${index}-${photo.url}`,
    queNo: index + 1,
    label: photo.label,
    isPhoto: true,
    links: [{ url: photo.url, fileName: photo.fileName }],
  }));

const TaskRoadmapDrawer = ({ open, taskId, onClose }) => {
  const { langMode, theme } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const t = translations[langMode];
  const isDark = theme === "dark";
  const isHighContrast = theme === "high-contrast";

  // `shouldRender` keeps the panel mounted through the slide-out animation;
  // `entered` drives the transform so the slide-in runs after first paint.
  const [shouldRender, setShouldRender] = useState(false);
  const [entered, setEntered] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // S3 link returned by the on-request PDF generation for a roadmap whose
  // payload had no `pdfLink`; cached so re-clicking reuses it.
  const [generatedPdfLink, setGeneratedPdfLink] = useState("");
  const pdfRequestInFlightRef = useRef(false);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setShouldRender(true);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    setEntered(false);
    closeTimerRef.current = setTimeout(() => setShouldRender(false), ANIMATION_MS);
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [open]);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  // Fetch on open / whenever a different task id is clicked. Aborting on
  // cleanup keeps a slow response for task A from overwriting task B's panel.
  useEffect(() => {
    if (!open || !taskId) return undefined;
    const controller = new AbortController();
    let active = true;

    const load = async () => {
      setIsLoading(true);
      setErrorMessage("");
      setRoadmap(null);
      setGeneratedPdfLink("");
      try {
        const responseData = await fetchTaskRoadmap(taskId, { signal: controller.signal });
        if (!active) return;
        if (responseData?.header?.code === 600) {
          setRoadmap(responseData?.body?.value || null);
        } else {
          setErrorMessage(
            responseData?.body?.value ??
            responseData?.body?.error ??
            responseData?.header?.message ??
            t.roadmapLoadFailed
          );
        }
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        console.error("Error fetching task roadmap:", error);
        setErrorMessage(getBackendErrorMessage(error) || t.roadmapLoadFailed);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    load();
    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Roadmaps for tasks whose report hasn't been generated yet come back with no
  // `pdfLink`; the download stays a plain download for the user -- the click
  // generates the report behind the scenes and opens the S3 link the backend
  // returns. The tab is opened synchronously (blank) before awaiting so the
  // browser still counts it as part of the click gesture and doesn't block it.
  const handleGeneratePdf = async () => {
    const requestTaskId = roadmap?.taskId ?? taskId;
    if (!requestTaskId || pdfRequestInFlightRef.current) return;

    pdfRequestInFlightRef.current = true;
    const pdfWindow = window.open("", "_blank");
    try {
      const { pdfLink, errorMessage: pdfError } = await generateTaskPdfLink({
        taskId: requestTaskId,
        accountNo: roadmap?.accountNo,
      });
      if (!pdfLink) {
        pdfWindow?.close();
        toast.error(pdfError || t.pdfnotavailable);
        return;
      }
      setGeneratedPdfLink(pdfLink);
      if (pdfWindow) {
        pdfWindow.location.href = pdfLink;
      } else {
        window.open(pdfLink, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error generating task PDF:", error);
      pdfWindow?.close();
      toast.error(getBackendErrorMessage(error) || t.failedtogeneratepdf);
    } finally {
      pdfRequestInFlightRef.current = false;
    }
  };

  if (!shouldRender) return null;

  const timeline = Array.isArray(roadmap?.timeline) ? roadmap.timeline : [];
  const areaLabel = [roadmap?.geoCode?.areaName, roadmap?.geoCode?.areaCode]
    .filter(hasValue)
    .join(" - ");
  const completedByLabel = (roadmap?.completedBy || [])
    .map(formatPerson)
    .filter(Boolean)
    .join(", ");
  const photoLinks = normalizePhotoLinks(roadmap?.photoLinks, t.roadmapPhotos);
  const fieldRows = normalizeCompletionFields(roadmap?.taskCompletionDetails ?? roadmap?.fields);
  const completionFields = fieldRows.length > 0 ? fieldRows : photoLinksAsCompletionFields(photoLinks);

  // Stored link from the roadmap payload, or the one just generated on request.
  const pdfLink = roadmap?.pdfLink || generatedPdfLink;
  const pdfButtonClass = `mb-5 inline-flex items-center gap-1.5 rounded-[10px] border px-3 py-2 text-xs font-semibold transition-colors ${isDark
    ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
    : "border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
    }`;

  const panelBg = isDark
    ? "bg-gray-900 border-gray-700"
    : isHighContrast
      ? "high-contrast border-2"
      : "bg-white border-slate-200";
  const headingText = isDark ? "text-gray-100" : "text-slate-800";
  const mutedText = isDark ? "text-gray-400" : "text-slate-500";

  const summaryItems = [
    { key: "workflow", Icon: Workflow, label: t.roadmapWorkflow, value: roadmap?.workflowName },
    { key: "account", Icon: Hash, label: t.roadmapAccountNo, value: roadmap?.accountNo },
    { key: "area", Icon: MapPin, label: t.roadmapArea, value: areaLabel },
    { key: "completedBy", Icon: User, label: t.roadmapCompletedBy, value: completedByLabel },
    {
      key: "createdAt",
      Icon: Clock3,
      label: t.roadmapCreatedAt,
      value: formatDateAndTime(roadmap?.createdDate, roadmap?.createdTime, roadmap?.createdAt),
    },
    {
      key: "finishedAt",
      Icon: Clock3,
      label: t.roadmapFinishedAt,
      value: formatDateAndTime(roadmap?.finishedDate, roadmap?.finishedTime, roadmap?.finishedAt),
    },
    { key: "reading", Icon: Gauge, label: t.roadmapReading, value: roadmap?.reading },
    { key: "remarks", Icon: StickyNote, label: t.roadmapRemarks, value: roadmap?.remarks },
    { key: "taskNote", Icon: StickyNote, label: t.roadmapTaskNote, value: roadmap?.taskNote, wide: true },
  ].filter((item) => hasValue(item.value));

  const isCompleted = String(roadmap?.status || "").trim().toLowerCase() === "completed";
  const showOutcomePill = isCompleted && typeof roadmap?.success === "boolean";
  // Only a completed task has a report to generate -- successful or not, but it
  // has to be finished. Anything still active/assigned gets no download button.
  const canGeneratePdf =
    Boolean(roadmap?.taskId ?? taskId) &&
    ["completed", "finished"].includes(String(roadmap?.status || "").trim().toLowerCase());
  const statusChipClass = isCompleted
    ? isDark
      ? "bg-gray-700 border-gray-600 text-gray-200"
      : "bg-slate-100 border-slate-200 text-slate-600"
    : isDark
      ? "bg-amber-500/15 border-amber-500/30 text-amber-400"
      : "bg-amber-50 border-amber-200 text-amber-700";

  return createPortal(
    <div className="fixed inset-0 z-[1200]" dir={isRtl ? "rtl" : "ltr"} role="dialog" aria-modal="true">
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${entered ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
        aria-hidden
      />

      <div
        className={`absolute top-0 ${isRtl ? "left-0" : "right-0"} flex h-full w-[94vw] max-w-[480px] flex-col border-l shadow-2xl transition-transform duration-300 ease-out ${panelBg} ${entered ? "translate-x-0" : isRtl ? "-translate-x-full" : "translate-x-full"
          }`}
      >
        {/* Header */}
        <div
          className={`flex shrink-0 items-start justify-between gap-3 border-b px-5 py-4 ${isDark ? "border-gray-700" : isHighContrast ? "border-2" : "border-slate-200"
            }`}
        >
          <div className="min-w-0">
            <h2 className={`text-base font-semibold ${headingText}`}>{t.taskRoadmap}</h2>
            <div className={`mt-0.5 flex flex-wrap items-center gap-2 text-xs ${mutedText}`}>
              <span className="font-medium">
                {t.taskId}: {roadmap?.taskId ?? taskId}
              </span>
              {hasValue(roadmap?.status) && (
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusChipClass}`}>
                  {roadmap.status}
                </span>
              )}
              {showOutcomePill && (
                <StatusPill success={roadmap.success} className="!px-2 !py-0.5 !text-[11px]" />
              )}
              {roadmap?.isDeleted && (
                <span
                  className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${isDark ? "bg-red-500/15 border-red-500/30 text-red-400" : "bg-red-50 border-red-200 text-red-700"
                    }`}
                >
                  {t.roadmapDeleted}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors ${isDark
              ? "border-gray-700 bg-gray-800 text-gray-300 hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-400"
              : "border-slate-200 bg-white text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              }`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-3">
              <svg
                className={`h-7 w-7 animate-spin ${isDark ? "text-gray-400" : "text-[#236DB4]"}`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className={`text-sm ${mutedText}`}>{t.roadmapLoading}</span>
            </div>
          ) : errorMessage ? (
            <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 text-center">
              <AlertCircle size={28} strokeWidth={1.5} className={isDark ? "text-gray-600" : "text-slate-300"} />
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-slate-600"}`}>{errorMessage}</p>
            </div>
          ) : !roadmap ? (
            <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 text-center">
              <CircleDot size={28} strokeWidth={1.5} className={isDark ? "text-gray-600" : "text-slate-300"} />
              <p className={`text-sm ${mutedText}`}>{t.roadmapNoData}</p>
            </div>
          ) : (
            <>
              {summaryItems.length > 0 && (
                <div className="mb-5">
                  <h3 className={`mb-2 text-[11px] font-semibold uppercase tracking-wide ${mutedText}`}>
                    {t.roadmapDetails}
                  </h3>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {summaryItems.map(({ key, Icon, label, value, wide }) => (
                      <div
                        key={key}
                        className={`flex items-start gap-2.5 rounded-[10px] border p-2.5 ${wide ? "sm:col-span-2" : ""} ${isDark
                          ? "border-gray-700 bg-gray-800/50"
                          : isHighContrast
                            ? "high-contrast border-2"
                            : "border-slate-200 bg-slate-50"
                          }`}
                      >
                        <div
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] ${isDark ? "bg-gray-700/70 text-gray-300" : "border border-slate-200 bg-white text-[#236DB4]"
                            }`}
                        >
                          <Icon size={13} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`mb-0.5 text-[10px] font-semibold uppercase tracking-wide ${mutedText}`}>
                            {label}
                          </div>
                          <div className={`break-words text-xs font-semibold ${isDark ? "text-gray-100" : "text-slate-800"}`}>
                            {value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {completionFields.length > 0 && (
                <div className="mb-5">
                  <h3 className={`mb-2 text-[11px] font-semibold uppercase tracking-wide ${mutedText}`}>
                    {t.roadmapCompletionInfo} ({completionFields.length})
                  </h3>
                  {/* Fixed-height, independently scrollable list so a task with
                      many captured fields can't push the timeline off screen. */}
                  <div
                    className={`max-h-[200px] overflow-y-auto rounded-[10px] border ${isDark
                      ? "border-gray-700 bg-gray-800/50"
                      : isHighContrast
                        ? "high-contrast border-2"
                        : "border-slate-200 bg-white"
                      }`}
                  >
                    <table className="w-full table-fixed text-left text-xs rtl:text-right">
                      <thead
                        className={`sticky top-0 z-10 text-[10px] font-semibold uppercase tracking-wide ${isDark
                          ? "bg-gray-800 text-gray-300"
                          : isHighContrast
                            ? "high-contrast"
                            : "bg-slate-50 text-slate-500"
                          }`}
                      >
                        <tr>
                          <th className="w-16 px-2 py-2 text-center font-semibold">{t.roadmapQueNo}</th>
                          <th className="w-[42%] px-3 py-2 font-semibold">{t.roadmapPhotoField}</th>
                          <th className="px-3 py-2 font-semibold">{t.roadmapFieldValue}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completionFields.map((field) => (
                          <tr
                            key={field.key}
                            className={
                              isDark
                                ? "border-t border-gray-700"
                                : isHighContrast
                                  ? "border-t-2"
                                  : "border-t border-slate-100"
                            }
                          >
                            <td
                              className={`px-2 py-2 align-top text-center text-[11px] font-semibold tabular-nums ${mutedText}`}
                            >
                              {field.queNo}
                            </td>
                            {/* [overflow-wrap:anywhere] so long field names and
                                unbroken S3 file names wrap inside the column
                                instead of forcing the drawer to scroll sideways,
                                then clamped to two lines with the full text in a
                                hover tooltip. */}
                            <td
                              className={`px-3 py-2 align-top font-medium ${isDark ? "text-gray-200" : "text-slate-700"}`}
                            >
                              <CommonTooltip text={field.label} position="bottom" disableDefaultDescription className="max-w-full">
                                <span className="line-clamp-2 [overflow-wrap:anywhere]">{field.label}</span>
                              </CommonTooltip>
                            </td>
                            <td className="px-3 py-2 align-top">
                              {field.isPhoto ? (
                                // multi_image fields carry several URLs — one
                                // link per line under the same field name.
                                <div className="flex flex-col gap-1">
                                  {field.links.map((link, linkIndex) => (
                                    <a
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`line-clamp-2 underline decoration-dotted underline-offset-[3px] [overflow-wrap:anywhere] transition-colors ${isDark
                                        ? "text-sky-400 hover:text-sky-300"
                                        : "text-[#236DB4] hover:text-[#1b5691]"
                                        }`}
                                    >
                                      {link.fileName}
                                      <ExternalLink size={11} className="ml-1 inline shrink-0 align-[-1px] opacity-70" />
                                    </a>
                                  ))}
                                </div>
                              ) : field.entries ? (
                                // Object answers: one "Key: value" line per
                                // entry, each clamped to a single line so a wide
                                // composite value can't blow up the row height.
                                <div className="flex flex-col gap-0.5">
                                  {field.entries.map((entry) => (
                                    <div key={entry.key} className="flex items-baseline gap-1">
                                      <CommonTooltip text={entry.label}  position="bottom" disableDefaultDescription className="w-20 shrink-0">
                                        <span
                                          className={`block w-full truncate text-[10px] font-semibold uppercase tracking-wide ${mutedText}`}
                                        >
                                          {entry.label}:
                                        </span>
                                      </CommonTooltip>
                                      <CommonTooltip text={entry.text} position="bottom" disableDefaultDescription className="min-w-0 flex-1">
                                        <span
                                          className={`block w-full truncate ${isDark ? "text-gray-100" : "text-slate-800"}`}
                                        >
                                          {entry.text}
                                        </span>
                                      </CommonTooltip>
                                    </div>
                                  ))}
                                </div>
                              ) : field.mailto ? (
                                <a
                                  href={field.mailto}
                                  className={`line-clamp-2 underline decoration-dotted underline-offset-[3px] [overflow-wrap:anywhere] transition-colors ${isDark ? "text-sky-400 hover:text-sky-300" : "text-[#236DB4] hover:text-[#1b5691]"
                                    }`}
                                >
                                  {field.text}
                                </a>
                              ) : (
                                <CommonTooltip text={field.text} position="bottom" disableDefaultDescription className="max-w-full">
                                  <span
                                    className={`line-clamp-2 [overflow-wrap:anywhere] whitespace-pre-line ${isDark ? "text-gray-100" : "text-slate-800"
                                      }`}
                                  >
                                    {field.text}
                                  </span>
                                </CommonTooltip>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {pdfLink ? (
                <a
                  href={pdfLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={pdfButtonClass}
                >
                  <Download size={13} />
                  {t.downloadPdf}
                </a>
              ) : canGeneratePdf ? (
                <button type="button" onClick={handleGeneratePdf} className={pdfButtonClass}>
                  <Download size={13} />
                  {t.downloadPdf}
                </button>
              ) : null}

              <h3 className={`mb-3 text-[11px] font-semibold uppercase tracking-wide ${mutedText}`}>
                {t.roadmapTimeline}
              </h3>

              {timeline.length === 0 ? (
                <p className={`py-6 text-center text-sm ${mutedText}`}>{t.roadmapNoTimeline}</p>
              ) : (
                <ol className="relative">
                  {timeline.map((item, index) => {
                    const { Icon, light, dark, rail } = getEventStyle(item);
                    const isLast = index === timeline.length - 1;
                    const lat = item?.meta?.latitude;
                    const lng = item?.meta?.longitude;
                    const metaChips = [];
                    if (hasValue(item?.meta?.source)) {
                      metaChips.push(`${t.roadmapSource}: ${item.meta.source}`);
                    }
                    if (hasValue(lat) && hasValue(lng)) {
                      metaChips.push(`${t.roadmapLocation}: ${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`);
                    }
                    const targetLabel = formatPerson(item?.target);
                    const actorLabel = formatPerson(item?.actor);
                    if (targetLabel) {
                      metaChips.push(`${t.roadmapAssignedTo}: ${targetLabel}`);
                    }
                    if (actorLabel) {
                      metaChips.push(`${t.roadmapBy}: ${actorLabel}`);
                    }

                    return (
                      <li key={item?.sequence ?? index} className="relative flex gap-3 pb-5 last:pb-0">
                        {/* Connector rail — skipped on the last node so the line
                            stops at the final event instead of dangling. */}
                        {!isLast && (
                          <span
                            aria-hidden
                            className={`absolute top-8 h-[calc(100%-1.5rem)] w-px ${isRtl ? "right-[15px]" : "left-[15px]"} ${isDark ? "bg-gray-700" : "bg-slate-200"
                              }`}
                          />
                        )}
                        <div
                          className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${isDark ? dark : light
                            }`}
                        >
                          <Icon size={15} />
                        </div>
                        <div
                          className={`min-w-0 flex-1 rounded-[10px] border p-3 ${isDark
                            ? "border-gray-700 bg-gray-800/50"
                            : isHighContrast
                              ? "high-contrast border-2"
                              : "border-slate-200 bg-white shadow-sm"
                            }`}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${rail}`} aria-hidden />
                            <span className={`text-sm font-semibold ${isDark ? "text-gray-100" : "text-slate-800"}`}>
                              {item?.label || item?.event}
                            </span>
                          </div>
                          <div className={`mt-1 flex items-center gap-1.5 text-[11px] ${mutedText}`}>
                            <Clock3 size={11} className="shrink-0" />
                            {formatEventStamp(item)}
                          </div>
                          {metaChips.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {metaChips.map((chip) => (
                                <span
                                  key={chip}
                                  className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${isDark ? "bg-gray-700/60 text-gray-300" : "bg-slate-100 text-slate-600"
                                    }`}
                                >
                                  {chip}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TaskRoadmapDrawer;
